"use client";

import { FormEvent, useRef, useState } from "react";
import { genericAdminStaffMfaError } from "../../lib/adminStaffMfa";
import CommerceDialog from "./CommerceDialog";

export default function StaffStepUpVerification({
  onCancel,
  onVerified,
}: {
  onCancel: () => void;
  onVerified: () => void;
}) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!/^\d{6}$/.test(code.trim())) {
      setError("Enter the 6-digit code from your Vintera authenticator.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const response = await fetch("/api/admin/staff/mfa/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: "step_up", code: code.trim() }),
      });
      const result = await response.json();
      if (!response.ok || result.ok !== true) throw new Error("step_up_failed");
      setCode("");
      onVerified();
    } catch {
      setCode("");
      setError(genericAdminStaffMfaError);
      window.setTimeout(() => inputRef.current?.focus(), 0);
    } finally {
      setSubmitting(false);
    }
  }

  function cancel() {
    if (submitting) return;
    setCode("");
    setError("");
    onCancel();
  }

  return (
    <CommerceDialog open title="Verify it's you" description="Enter the current code from your Vintera authenticator before continuing." showHeader={false} busy={submitting} onClose={cancel}>
        <p className="text-xs font-bold uppercase tracking-wide text-[var(--vintera-sage-dark)]">Sensitive security action</p>
        <h2 id="staff-step-up-title" className="mt-2 text-2xl font-bold text-[var(--vintera-navy)]">Verify it&apos;s you</h2>
        <p id="staff-step-up-description" className="mt-3 text-sm leading-6 text-[var(--vintera-muted)]">
          For security, enter the current code from your Vintera authenticator before continuing. This verifies the signed-in staff member, not the account being managed.
        </p>
        <form action="/api/admin/staff/mfa/verify" method="post" onSubmit={submit} className="mt-5">
          <label htmlFor="staff-step-up-code" className="block text-sm font-semibold text-[var(--vintera-navy)]">
            Authentication code
          </label>
          <input
            ref={inputRef}
            data-dialog-focus
            id="staff-step-up-code"
            name="code"
            type="password"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            aria-invalid={Boolean(error)}
            aria-describedby={error ? "staff-step-up-error" : undefined}
            className="mt-2 w-full rounded-md border border-[var(--vintera-border)] px-4 py-3 text-lg tracking-[0.3em] outline-none focus:border-[var(--vintera-navy)]"
          />
          {error && <p id="staff-step-up-error" role="alert" className="mt-3 rounded-md bg-red-100 p-3 text-sm font-medium text-red-800">{error}</p>}
          <div className="mt-5 grid gap-2 sm:grid-cols-2">
            <button type="submit" disabled={submitting} className="min-h-11 rounded-md bg-[var(--vintera-navy)] px-4 py-3 text-sm font-semibold text-white disabled:opacity-60">
              {submitting ? "Verifying…" : "Verify"}
            </button>
            <button type="button" disabled={submitting} onClick={cancel} className="min-h-11 rounded-md border border-[var(--vintera-border)] px-4 py-3 text-sm font-semibold disabled:opacity-60">
              Cancel
            </button>
          </div>
        </form>
    </CommerceDialog>
  );
}
